/**
 * @fileoverview The four summary tiles at the top of the Sales Dashboard.
 * @description Active clients, In the backlog, Team achievement and Needs attention, each shown
 * only when the backend sent its section (a section the caller may not see is left out, never
 * zeroed). Every figure is the one the API returned; nothing is derived or summed here.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import KpiStrip from '@/shared/components/molecules/KpiStrip'

import type { SalesDashboard } from '@/features/sales/types/sales-dashboard.types'
import type { ComponentProps } from 'react'

type KpiTiles = ComponentProps<typeof KpiStrip>['tiles']

interface DashboardKpiTilesProps {
  dashboard: SalesDashboard
}

/**
 * @component
 * @param {DashboardKpiTilesProps} props - The dashboard.
 * @param {SalesDashboard} props.dashboard - The dashboard the backend returned.
 */
function DashboardKpiTiles({ dashboard }: DashboardKpiTilesProps) {
  const { t } = useTranslation()
  const { activeClients, backlog, achievement, needsAttention } = dashboard
  const tiles: KpiTiles = []

  if (activeClients) {
    tiles.push({
      label: t('sales.dashboard.kpi.activeClients'),
      sublabel: t('sales.dashboard.kpi.activeClientsSublabel', {
        live: activeClients.live,
        onboarding: activeClients.onboarding,
      }),
      icon: 'briefcase',
      iconTone: 'navy',
      value: activeClients.total,
      testId: 'dashboard-kpi-active-clients',
    })
  }
  if (backlog) {
    tiles.push({
      label: t('sales.dashboard.kpi.backlog'),
      sublabel: t('sales.dashboard.kpi.backlogSublabel', { hot: backlog.hot }),
      icon: 'filter',
      iconTone: 'blue',
      value: backlog.total,
      testId: 'dashboard-kpi-backlog',
    })
  }
  if (achievement) {
    tiles.push({
      label: t('sales.dashboard.kpi.teamAchievement'),
      sublabel: t('sales.dashboard.kpi.teamAchievementSublabel'),
      icon: 'trend',
      iconTone: 'green',
      value: `${achievement.percent}%`,
      testId: 'dashboard-kpi-team-achievement',
      tone: 'warning',
    })
  }
  if (needsAttention) {
    tiles.push({
      label: t('sales.dashboard.kpi.needsAttention'),
      sublabel: t('sales.dashboard.kpi.needsAttentionSublabel'),
      icon: 'info',
      iconTone: 'amber',
      value: needsAttention.count,
      testId: 'dashboard-kpi-needs-attention',
      tone: 'danger',
    })
  }

  return tiles.length === 0 ? null : <KpiStrip tiles={tiles} />
}

export default DashboardKpiTiles
